import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { selectClassifications } from '../../../account-info-header/reducers';
import { ComponentState } from '../../../shared/types/ComponentState';
import { Hyperlink } from '../../../shared/types/Hyperlink';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { transactionDirection } from '../../../shared/utilities/Constants';
import { FormControlRadio, RadioControl, WarningText } from '../../models/form/FormControlRadio';
import { selectTransactionForm } from '../../reducers';

@Component({
  standalone: false,
  selector: 'mm-ach-transfer-direction',
  templateUrl: './ach-transfer-direction.component.html',
  styleUrls: ['./ach-transfer-direction.component.scss']  
})
export class AchTransferDirectionComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() instructions$!: Observable<StandingInstruction[]>;
  isIip: boolean;
  formState$: Observable<ComponentState>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  transferDirectionModel: RadioControl = {
    title: 'Transfer direction',
    controlName: 'transferDirection',
    radios: [],
    validationMessage: 'Select a transfer direction'
  };

  noInstructionsErrorLink: Hyperlink = {
    url: '',
    text: 'Set up a new account.',
    target: '_blank',
    isActionCenterHyperLink: true
  }

  constructor(private store: Store) { }

  ngOnInit(): void {
    this.formState$ = this.store.select(selectTransactionForm).pipe(take(2));
    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.isIip = val.isIip;
    })

    this.instructions$.pipe(takeUntil(this.destroy$)).subscribe((instructions) => {
      this.transferDirectionModel.radios = [];
      if (instructions.length > 0) {
        this.transferDirectionModel.radios = this.transferDirectionRadios;
      }

      const incoming = instructions.filter(instruction => instruction.fundsDirection === transactionDirection.in);
      if (incoming.length === 0 && this.transferDirectionModel.radios.length > 0) {
        const incomingRadio = this.transferDirectionModel.radios[0];
        incomingRadio.disabled = true;
        incomingRadio.warning = this.getTransferDirectionWarning(transactionDirection.in.toLowerCase());
        // If the value is already set - we don't need to re-set the value and emit (it will break pre-loading/edit flow)
        if (!(this.transferDirectionControl.value === transactionDirection.out.toLowerCase()) && !this.isIip) {
          this.transferDirectionControl.patchValue(transactionDirection.out.toLowerCase());
          this.transferDirectionControl.markAsTouched();
          this.transferDirectionControl.updateValueAndValidity();
        }
      }

      const outgoing = instructions.filter(instruction => instruction.fundsDirection === transactionDirection.out);
      if (outgoing.length === 0 && this.transferDirectionModel.radios.length > 0) {
        const outgoingRadio = this.transferDirectionModel.radios[1];
        outgoingRadio.disabled = true;
        outgoingRadio.warning = this.getTransferDirectionWarning(transactionDirection.out.toLowerCase());
        // If the value is already set - we don't need to re-set the value and emit (it will break pre-loading/edit flow)
        if (!(this.transferDirectionControl.value === transactionDirection.in.toLowerCase())) {
          this.transferDirectionControl.patchValue(transactionDirection.in.toLowerCase());
          this.transferDirectionControl.markAsTouched();
          this.transferDirectionControl.updateValueAndValidity();
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private getTransferDirectionWarning(direction: string): WarningText {
    return {
      text: `To create an ${direction} transaction, `,
      link: {
        text: 'set up a new account.',
        url: '',
        target: '_blank',
        isActionCenterHyperLink: true
      }
    }
  }

  get transferDirectionRadios(): FormControlRadio[] {
    if (!this.isIip) {
      return [{
        value: 'incoming',
        label: ['Incoming'],
      },
      {
        value: 'outgoing',
        label: ['Outgoing'],
      }];
    }
    return [{
      value: 'incoming',
      label: ['Incoming'],
    }];
  }

  get transferDirectionControl(): UntypedFormControl {
    return this.parentForm.get('transferDirection') as UntypedFormControl;
  }
}
